'use client';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DoctorGridSkeleton } from '@/helpers/skeleton/SDoctors';
import { imageSrc } from '@/lib/imageSrc';
import { useGetDoctorsQuery } from '@/redux/api/doctorApi';
import { Calendar, Info, Mail, Phone } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { Images } from '../../../public/assets';

interface ContactInfo {
  email: string;
  phone: string;
}

interface Availability {
  days: string[];
  time: string;
}

interface Doctor {
  _id: string;
  name: string;
  specialty: string;
  description?: string;
  image: string;
  contact: ContactInfo;
  availability: Availability;
}

const DoctorCard = () => {
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const { data, isError, isLoading } = useGetDoctorsQuery(undefined);
  const doctors = data?.body as Doctor[];

  if (isLoading) return <DoctorGridSkeleton />;
  if (isError) return <ErrorState />;
  if (!doctors?.length) return <EmptyState />;

  return (
    <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {doctors.map((doctor, index) => (
          <DoctorProfileCard
            key={index}
            doctor={doctor}
            onSelect={() => setSelectedDoctor(doctor)}
          />
        ))}
      </div>

      <DoctorDetailsModal
        doctor={selectedDoctor}
        onClose={() => setSelectedDoctor(null)}
      />
    </>
  );
};

interface DoctorProfileCardProps {
  doctor: Doctor;
  onSelect: () => void;
}

const DoctorProfileCard = ({ doctor, onSelect }: DoctorProfileCardProps) => {
  return (
    <div className="group relative h-full overflow-hidden rounded-lg bg-white transition-all duration-300">
      <div className="relative aspect-square w-full overflow-hidden">
        <Image
          onClick={onSelect}
          src={imageSrc(doctor.image) || Images.fullLogo}
          alt={doctor.name}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          loading="lazy"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <ContactButtons doctor={doctor} />
      </div>

      <div onClick={onSelect} className="cursor-pointer p-4 transition-colors">
        <h3 className="text-lg font-semibold text-gray-900">{doctor.name}</h3>
        <p className="text-sm text-gray-800">{doctor.specialty}</p>
      </div>
    </div>
  );
};

interface ContactButtonsProps {
  doctor: Doctor;
}

const ContactButtons = ({ doctor }: ContactButtonsProps) => {
  return (
    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 translate-y-8 items-center gap-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
      <a
        href={`mailto:${doctor.contact.email}`}
        className="hover:text-primary-dark flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-primary shadow-md transition-all hover:bg-white hover:shadow-lg"
        aria-label={`Email ${doctor.name}`}
      >
        <Mail className="h-5 w-5" />
      </a>
      <a
        href={`tel:${doctor.contact.phone}`}
        className="hover:text-primary-dark flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-primary shadow-md transition-all hover:bg-white hover:shadow-lg"
        aria-label={`Call ${doctor.name}`}
      >
        <Phone className="h-5 w-5" />
      </a>
    </div>
  );
};

interface DoctorDetailsModalProps {
  doctor: Doctor | null;
  onClose: () => void;
}

export const DoctorDetailsModal = ({
  doctor,
  onClose,
}: DoctorDetailsModalProps) => {
  if (!doctor) return null;

  return (
    <Dialog open={!!doctor} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <ScrollArea className="max-h-[80vh] pr-4">
          <div className="space-y-6">
            <DialogTitle>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {doctor.name}
                </h2>
                <p className="text-primary">{doctor.specialty}</p>
              </div>
            </DialogTitle>
            <div className="grid gap-6 md:grid-cols-1">
              <DoctorImage image={doctor.image} name={doctor.name} />

              <div className="space-y-4">
                {doctor.description && (
                  <div className="space-y-2">
                    <h3 className="flex items-center gap-2 text-lg font-semibold">
                      <Info size={18} />
                      About
                    </h3>
                    <p className="text-gray-700">{doctor.description}</p>
                  </div>
                )}

                <ContactInfo contact={doctor.contact} />
              </div>
            </div>

            <AvailabilitySchedule availability={doctor.availability} />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

interface DoctorImageProps {
  image: string;
  name: string;
}

const DoctorImage = ({ image, name }: DoctorImageProps) => (
  <div className="relative mx-auto aspect-square w-full max-w-[400px] overflow-hidden rounded-lg bg-white">
    <Image
      src={imageSrc(image)}
      alt={name}
      fill
      className="object-cover"
      priority
    />
  </div>
);

interface ContactInfoProps {
  contact: ContactInfo;
}

const ContactInfo = ({ contact }: ContactInfoProps) => (
  <div className="space-y-3">
    <h3 className="text-lg font-semibold">Contact Information</h3>
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-gray-500" />
        <a
          href={`mailto:${contact.email}`}
          className="text-primary hover:underline"
        >
          {contact.email}
        </a>
      </div>
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-gray-500" />
        <a
          href={`tel:${contact.phone}`}
          className="text-primary hover:underline"
        >
          {contact.phone}
        </a>
      </div>
    </div>
  </div>
);

interface AvailabilityScheduleProps {
  availability: Availability;
}

const AvailabilitySchedule = ({ availability }: AvailabilityScheduleProps) => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  return (
    <div className="space-y-3">
      <h3 className="flex items-center gap-2 text-lg font-semibold">
        <Calendar size={18} />
        Availability
      </h3>

      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
        {days.map((day) => {
          const isAvailable = availability.days.includes(day.toLowerCase());

          return (
            <div
              key={day}
              className={`flex items-center justify-between p-3 ${isAvailable ? '' : 'bg-gray-50'}`}
            >
              <span className={isAvailable ? 'font-medium' : 'text-gray-500'}>
                {day}
              </span>
              <span className={isAvailable ? 'text-primary' : 'text-gray-400'}>
                {isAvailable ? availability.time : 'Not available'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ErrorState = () => (
  <div className="flex flex-col items-center justify-center space-y-2 py-12 text-center">
    <div className="text-red-500">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
    </div>
    <h3 className="text-lg font-medium">Something went wrong</h3>
    <p className="text-sm text-muted-foreground">
      We couldn&rsquo;t load the doctors list. Please try again later.
    </p>
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center space-y-2 py-12 text-center">
    <div className="text-gray-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path>
        <path d="M9 10h6"></path>
        <path d="M9 14h6"></path>
      </svg>
    </div>
    <h3 className="text-lg font-medium">No doctors available</h3>
    <p className="text-sm text-muted-foreground">
      There are currently no doctors to display.
    </p>
  </div>
);

export default DoctorCard;
